import {
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { VideoMediaRequest } from '@api'
import { useUnit } from 'effector-react'

import CloseIcon from '@/assets/icons/icon-close.svg'
import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { DIALOG_TYPE } from '@/dialogs/constants.ts'
import { SuggestionDtoWithTranslation } from '@/models/chattingUI/chatSuggestions/chatSuggestions'
import { openDialog } from '@/models/dialogs.ts'
import { $vaultMediasMap, loadMultipleMediasFx } from '@/models/vault/medias'
import ActionPriceTag from '@/modules/chatting/icons/action-price-tag.svg'
import { useSettingsContext } from '@/modules/creator-settings/state/SettingsProvider.tsx'
import { VaultMediaItem } from '@/modules/vault/ui/media/vault-media-item.tsx'
import { VaultMediaPlaceholder } from '@/modules/vault/ui/media/vault-media-placeholder.tsx'
import { sanitizeHTML } from '@/utils/html'
import { noop } from '@/utils/misc.ts'

import type { DialogProps } from '../types'

export interface Props extends DialogProps {
  suggestion: SuggestionDtoWithTranslation
  messageIndex: number
  currentPrice: Record<number, number>
  handlePricesChange: (index: number, feedback: Record<number, number>) => void
}

export const ChattingSuggestionsDialog: FC<Props> = ({
  isOpen,
  onClose,
  suggestion,
  handlePricesChange,
  messageIndex,
  currentPrice,
}) => {
  const vaultMediasMap = useUnit($vaultMediasMap)
  const { messageMinPrice, messageMaxPrice } = useSettingsContext()
  const [activeTab, setActiveTab] = useState('0')
  const loadedMediaIdsRef = useRef<Set<number>>(new Set())
  const [localPrices, setLocalPrices] = useState<Record<number, number>>(() => {
    const pricesFromMessages: Record<number, number> = {}

    suggestion?.messages?.forEach((message, index) => {
      if (message.price !== undefined && message.price !== null) {
        pricesFromMessages[index] = message.price
      }
    })

    return {
      ...pricesFromMessages,
      ...(currentPrice || {}),
    }
  })

  const mediaIds = useMemo(() => {
    const ids: number[] = []

    suggestion?.messages?.forEach((m) => {
      const medias = m.mediaFiles

      if (medias) {
        const mappedIds = medias
          .map((el) => {
            if (!el) {
              return null
            }

            return typeof el === 'number'
              ? (el as number)
              : (el as VideoMediaRequest).id
          })
          .filter((id): id is number => !!id)

        ids.push(...mappedIds)
      }
    })

    const result = ids.filter(Boolean)

    return result
  }, [suggestion?.messages])

  useEffect(() => {
    if (!isOpen || !mediaIds.length) {
      return
    }

    const missingMediaIds = mediaIds.filter(
      (id) => !vaultMediasMap[id] && !loadedMediaIdsRef.current.has(id),
    )

    if (missingMediaIds.length > 0) {
      missingMediaIds.forEach((id) => loadedMediaIdsRef.current.add(id))
      loadMultipleMediasFx(missingMediaIds)
    }
  }, [isOpen, vaultMediasMap, mediaIds])

  useEffect(() => {
    handlePricesChange(messageIndex, localPrices)
  }, [handlePricesChange, messageIndex, localPrices])

  const processedSuggestions = useMemo(() => {
    if (!suggestion?.messages) {
      return { messages: [] }
    }

    return {
      messages: suggestion.messages.map((message) => ({
        ...message,
        mediaFiles: message.mediaFiles?.map((media) => ({
          data:
            typeof media === 'number'
              ? vaultMediasMap[media]
              : vaultMediasMap[(media as VideoMediaRequest).id],
        })),
      })),
    }
  }, [suggestion, vaultMediasMap])

  const handlePriceDialogOpen = useCallback(
    (index: number) => {
      openDialog({
        type: DIALOG_TYPE.CHAT_PRICE,
        onSave: (price) => {
          setLocalPrices((prev) => ({ ...prev, [index]: price }))
        },
        defaultPrice: localPrices[index],
        messageMinPrice: messageMinPrice,
        messageMaxPrice: messageMaxPrice,
        userId: 0,
      })
    },
    [messageMaxPrice, messageMinPrice, localPrices],
  )

  if (!suggestion?.messages?.length) {
    return (
      <Dialog
        show={isOpen}
        onClose={onClose}
        childrenClassName="max-w-md"
      >
        <div className="space-y-6">
          <Dialog.Header>
            <Dialog.Title>No Messages Available</Dialog.Title>
          </Dialog.Header>
          <div className="p-6 text-center">
            <p className="text-gray-600 dark:text-gray-400">
              There are no messages available for preview.
            </p>
          </div>
          <div className="w-full">
            <Button
              onClick={onClose}
              variant="outline"
              className="w-full"
            >
              Close
            </Button>
          </div>
        </div>
      </Dialog>
    )
  }

  return (
    <Dialog
      show={isOpen}
      onClose={onClose}
      bigDialog
      childrenClassName="max-w-full overflow-hidden"
    >
      <div className="space-y-6">
        <Dialog.Header>
          <Dialog.Title>Media preview</Dialog.Title>
        </Dialog.Header>

        <div className="flex flex-col gap-6 w-full">
          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
          >
            <TabsList>
              {processedSuggestions.messages?.map((message, index) => (
                <TabsTrigger
                  key={`tab-${messageIndex}-${index}`}
                  value={index.toString()}
                >
                  {index + 1}
                </TabsTrigger>
              ))}
            </TabsList>

            {processedSuggestions.messages?.map((message, index) => (
              <TabsContent
                key={`content-${messageIndex}-${index}`}
                value={index.toString()}
              >
                <div className="p-2 flex flex-col no-wrap gap-6 items-center h-full max-h-[60vh] overflow-y-scroll overflow-x-hidden w-full">
                  <div className="shrink-0 max-w-full w-full">
                    <div className="flex gap-2 items-center justify-between">
                      {message.text ? (
                        <div
                          className="p-2 bg-gray-100 dark:bg-slate-600 rounded-xl my-6 w-full"
                          dangerouslySetInnerHTML={{
                            __html: sanitizeHTML(
                              message.translatedText || message.text || '',
                              false,
                            ),
                          }}
                        />
                      ) : (
                        <div className="p-2 bg-gray-100 dark:bg-red-400 rounded-xl my-6 w-full">
                          No text in this message
                        </div>
                      )}

                      <div
                        onClick={
                          message.mediaFiles?.length
                            ? () => handlePriceDialogOpen(index)
                            : undefined
                        }
                        className={`flex items-center rounded px-2.5 py-2 min-w-[210px] ${
                          message.mediaFiles?.length
                            ? 'bg-primary-500 cursor-pointer'
                            : 'bg-gray-500 cursor-not-allowed opacity-50'
                        }`}
                      >
                        <ActionPriceTag className="h-5 w-5 text-white" />
                        <span className="mx-2 text-sm font-medium text-white">
                          Price to View: ${localPrices[index] || 'Free'}
                        </span>
                        <CloseIcon className="text-secondary-100 cursor-pointer" />
                      </div>
                    </div>

                    <div className="max-w-full overflow-x-scroll">
                      <div className="flex gap-4 p-2">
                        {message.mediaFiles
                          ?.filter(Boolean)
                          .map((media, index) => {
                            const mediaData = media?.data

                            if (!mediaData?.id || !mediaData?.files) {
                              return (
                                <VaultMediaPlaceholder
                                  iconWidth={100}
                                  key={`no-media-${index}`}
                                  className="min-w-[200px] h-[200px] border-secondary-100 dark:border-secondary-500 border rounded"
                                  body={
                                    <div className="flex flex-col items-center justify-center text-center">
                                      <span>Unexpected error occurred</span>
                                    </div>
                                  }
                                />
                              )
                            }

                            return (
                              <div
                                key={`media-${mediaData.id}`}
                                className="shrink-0"
                              >
                                <VaultMediaItem
                                  className="w-[200px] h-[200px]"
                                  item={mediaData}
                                  showSettings={false}
                                  showBackground
                                  onMediaPreview={noop}
                                />
                              </div>
                            )
                          })}
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
        <div className="w-full">
          <Button
            onClick={onClose}
            variant="outline"
            className="w-full"
          >
            Close
          </Button>
        </div>
      </div>
    </Dialog>
  )
}
